import apiClient from '../api/apiClient';
import type {
  Dashboard,
  DashboardWidget,
  WidgetTemplate,
  Page,
  CreateDashboardRequest,
  UpdateDashboardRequest,
  CreateWidgetRequest,
  UpdateWidgetRequest,
  BulkLayoutUpdateRequest,
  CloneWidgetRequest,
  MoveWidgetRequest,
  CloneDashboardRequest,
  SharedDashboard,
  SharedDashboardDetail,
  DashboardShare,
  DashboardPublicLink,
  DefaultDashboardResponse,
} from '../types/widget';

export interface CreateWidgetFromTemplateRequest {
  template_id: string;
  custom_title?: string;
  query_config_overrides?: Record<string, unknown>;
  visualization_config_overrides?: Record<string, unknown>;
  position_x: number;
  position_y: number;
  sort_index?: number;
}

// Helper to build page-scoped UI API path
const pagePath = (pageKey: string, path: string) => `/ui/pages/${pageKey}${path}`;

// Helper to build direct dashboard path
const dashboardPath = (path: string) => `/ui/dashboards${path}`;

// Helper to build direct widget path
const widgetPath = (path: string) => `/ui/widgets${path}`;

// API response wrapper type
interface ApiListResponse<T> {
  count: number;
  data: T[];
  message?: string;
}

// Helper to extract data from wrapped response
const extractData = <T>(response: { data: T[] | ApiListResponse<T> }): T[] => {
  if (!response || !response.data) return [];

  const data = response.data;
  if (Array.isArray(data)) {
    return data as T[];
  }
  return (data.data || []) as T[];
};

// Helper to extract single item from response
const extractSingle = <T>(response: { data: T | { data: T } }): T => {
  const responseData = response.data as Record<string, unknown>;

  if (
    responseData &&
    typeof responseData === 'object' &&
    'data' in responseData &&
    responseData.data !== null &&
    typeof responseData.data === 'object' &&
    !Array.isArray(responseData.data)
  ) {
    return responseData.data as T;
  }

  return responseData as T;
};

// ============================================
// Dashboard API Service
// ============================================

export const dashboardService = {
  async getDashboardsByPageKey(pageKey: string): Promise<Dashboard[]> {
    const response = await apiClient.get(pagePath(pageKey, `/dashboards`));
    return extractData<Dashboard>(response);
  },

  /**
   * Get the default dashboard for a page (creates one if none exists)
   * Returns dashboard + widgets in a single call, avoiding race conditions
   */
  async getDefaultDashboard(pageKey: string): Promise<DefaultDashboardResponse> {
    const response = await apiClient.get(pagePath(pageKey, `/dashboards/default`));
    // response.data is axios wrapper
    // API may return: { data: { data: dashboard, widgets: [...], is_new } }
    // or: { data: dashboard, widgets: [...], is_new }
    const wrapper = response.data;

    // If wrapper.data exists and has an id (it's the dashboard), widgets are at wrapper level
    // If wrapper.data.data exists, that's the dashboard and widgets are at wrapper.data level
    if (wrapper?.data?.data && typeof wrapper.data.data === 'object') {
      // Double-wrapped: { data: { data: dashboard, widgets, is_new } }
      return {
        dashboard: wrapper.data.data,
        widgets: wrapper.data.widgets || [],
        is_new: wrapper.data.is_new || false,
      };
    }

    if (wrapper?.data && typeof wrapper.data === 'object' && 'id' in wrapper.data) {
      // Single-wrapped with dashboard in data: { data: dashboard, widgets, is_new }
      return {
        dashboard: wrapper.data,
        widgets: wrapper.widgets || [],
        is_new: wrapper.is_new || false,
      };
    }

    // Fallback: assume wrapper is the response object with data as dashboard
    return {
      dashboard: wrapper?.data || wrapper,
      widgets: wrapper?.widgets || [],
      is_new: wrapper?.is_new || false,
    };
  },

  async getMyDashboards(): Promise<Dashboard[]> {
    const response = await apiClient.get(dashboardPath(`/mine`));
    return extractData<Dashboard>(response);
  },

  async getSharedDashboards(): Promise<Dashboard[]> {
    const response = await apiClient.get(dashboardPath(`/shared`));
    return extractData<Dashboard>(response);
  },

  async getDashboard(id: number): Promise<Dashboard> {
    const response = await apiClient.get(dashboardPath(`/${id}`));
    return extractSingle<Dashboard>(response);
  },

  async createDashboard(pageKey: string, data: CreateDashboardRequest): Promise<Dashboard> {
    const response = await apiClient.post(pagePath(pageKey, `/dashboards`), {
      page_id: data.page_id,
      name: data.name,
      description: data.description,
      is_default: data.is_default || false,
      sort_index: data.sort_index || 0,
    });
    return extractSingle<Dashboard>(response);
  },

  async updateDashboard(id: number, data: UpdateDashboardRequest): Promise<Dashboard> {
    const response = await apiClient.put(dashboardPath(`/${id}`), {
      name: data.name,
      description: data.description,
      is_default: data.is_default,
      sort_index: data.sort_index,
    });
    return extractSingle<Dashboard>(response);
  },

  async updateDashboardName(id: number, name: string): Promise<Dashboard> {
    const response = await apiClient.patch(dashboardPath(`/${id}/name`), { name });
    return extractSingle<Dashboard>(response);
  },

  async deleteDashboard(id: number): Promise<void> {
    await apiClient.delete(dashboardPath(`/${id}`));
  },

  async setDefaultDashboard(id: number, pageId: number): Promise<void> {
    await apiClient.post(dashboardPath(`/${id}/set-default`), { page_id: pageId });
  },

  async cloneDashboard(id: number, data: CloneDashboardRequest): Promise<Dashboard> {
    const response = await apiClient.post(dashboardPath(`/${id}/clone`), {
      target_page_id: data.target_page_id,
      new_name: data.new_name,
      sort_index: data.sort_index,
    });
    return extractSingle<Dashboard>(response);
  },

  // ============================================
  // Dashboard Sharing APIs
  // ============================================

  /**
   * Get all dashboards shared with the current user
   */
  async getDashboardsSharedWithMe(): Promise<SharedDashboard[]> {
    const response = await apiClient.get(dashboardPath('/shared-with-me'));
    return extractData<SharedDashboard>(response);
  },

  /**
   * Get a shared dashboard with owner context
   */
  async getSharedDashboardDetail(id: number): Promise<SharedDashboardDetail> {
    const response = await apiClient.get(dashboardPath(`/${id}/shared`));
    return extractSingle<SharedDashboardDetail>(response);
  },

  /**
   * Share a dashboard with another user
   * @param id - Dashboard ID
   * @param sharedWithUserUuid - User's snowflake ID (user_uuid)
   */
  async shareDashboard(id: number, sharedWithUserUuid: string): Promise<DashboardShare> {
    const response = await apiClient.post(dashboardPath(`/${id}/share`), {
      shared_with_user_id: sharedWithUserUuid,
    });
    return extractSingle<DashboardShare>(response);
  },

  /**
   * Revoke a user's access to a shared dashboard
   * @param dashboardId - Dashboard ID
   * @param userUuid - User's snowflake ID (user_uuid)
   */
  async revokeDashboardShare(dashboardId: number, userUuid: string): Promise<void> {
    await apiClient.delete(dashboardPath(`/${dashboardId}/share/${userUuid}`));
  },

  /**
   * List all users a dashboard is shared with
   */
  async getDashboardShares(id: number): Promise<DashboardShare[]> {
    const response = await apiClient.get(dashboardPath(`/${id}/shares`));
    return extractData<DashboardShare>(response);
  },

  /**
   * Create a public link for a dashboard
   */
  async createPublicLink(
    id: number,
    options?: { shared_email?: string; expires_at?: string }
  ): Promise<DashboardPublicLink> {
    const response = await apiClient.post(dashboardPath(`/${id}/public-link`), options || {});
    return extractSingle<DashboardPublicLink>(response);
  },

  /**
   * List all public links for a dashboard
   */
  async getPublicLinks(id: number): Promise<DashboardPublicLink[]> {
    const response = await apiClient.get(dashboardPath(`/${id}/public-links`));
    return extractData<DashboardPublicLink>(response);
  },

  /**
   * Deactivate a public link
   */
  async deactivatePublicLink(dashboardId: number, linkId: string): Promise<void> {
    await apiClient.delete(dashboardPath(`/${dashboardId}/public-link/${linkId}`));
  },
};

// ============================================
// User API Service (for dashboard sharing)
// ============================================

export interface AccountUser {
  id: number;
  /** Snowflake ID - used for API requests */
  user_uuid: string;
  first_name: string;
  last_name: string;
  email: string;
}

export const userService = {
  /**
   * Get all users in the account (for user picker in share dialog)
   * Uses: GET /core/api/v1/admin/users/
   */
  async getAccountUsers(): Promise<AccountUser[]> {
    const response = await apiClient.get('/admin/users/');
    return response.data?.users || [];
  },
};

// ============================================
// Widget API Service
// ============================================

// Share destination type for widget sharing
export interface ShareDestination {
  id: number;
  name: string;
  page_id: number;
  page_key: string;
  page_type: 'global' | 'user_landing' | 'model';
  page_name: string;
  page_model_key: string | null;
}

export const widgetService = {
  async getWidgetsByDashboard(dashboardId: number): Promise<DashboardWidget[]> {
    const response = await apiClient.get(dashboardPath(`/${dashboardId}/widgets`));
    return extractData<DashboardWidget>(response);
  },

  async getVisibleWidgets(dashboardId: number): Promise<DashboardWidget[]> {
    const response = await apiClient.get(dashboardPath(`/${dashboardId}/widgets`), {
      params: { visible_only: true },
    });
    return extractData<DashboardWidget>(response);
  },

  async getWidget(id: number): Promise<DashboardWidget> {
    const response = await apiClient.get(widgetPath(`/${id}`));
    return extractSingle<DashboardWidget>(response);
  },

  async createWidget(dashboardId: number, data: Omit<CreateWidgetRequest, 'dashboard_id'>): Promise<DashboardWidget> {
    const response = await apiClient.post(dashboardPath(`/${dashboardId}/widgets`), {
      source_type: data.source_type,
      title: data.title,
      query_config: data.query_config,
      visualization_config: data.visualization_config,
      position_x: data.position_x,
      position_y: data.position_y,
      width: data.width,
      height: data.height,
      sort_index: data.sort_index || 0,
      is_visible: data.is_visible ?? true,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async createWidgetFromTemplate(
    dashboardId: number,
    data: CreateWidgetFromTemplateRequest
  ): Promise<DashboardWidget> {
    const response = await apiClient.post(dashboardPath(`/${dashboardId}/widgets/from-template`), {
      template_id: data.template_id,
      custom_title: data.custom_title,
      query_config_overrides: data.query_config_overrides,
      visualization_config_overrides: data.visualization_config_overrides,
      position_x: data.position_x,
      position_y: data.position_y,
      sort_index: data.sort_index ?? 0,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async batchCreateWidgetsFromTemplate(
    dashboardId: number,
    widgets: Array<{
      template_id: string;
      custom_title?: string;
      position_x: number;
      position_y: number;
      sort_index: number;
    }>
  ): Promise<DashboardWidget[]> {
    const response = await apiClient.post(dashboardPath(`/${dashboardId}/widgets/batch-from-template`), {
      widgets,
    });
    return extractData<DashboardWidget>(response);
  },

  async createWidgetFromPublishedTemplate(
    dashboardId: number,
    data: {
      published_template_id: string;
      custom_title?: string;
      position_x?: number;
      position_y?: number;
      sort_index?: number;
      width?: number;
      height?: number;
    }
  ): Promise<DashboardWidget> {
    const response = await apiClient.post(dashboardPath(`/${dashboardId}/widgets/from-published-template`), data);
    return extractSingle<DashboardWidget>(response);
  },

  async updateWidget(id: number, data: UpdateWidgetRequest): Promise<DashboardWidget> {
    const response = await apiClient.put(widgetPath(`/${id}`), {
      title: data.title,
      query_config: data.query_config,
      visualization_config: data.visualization_config,
      position_x: data.position_x,
      position_y: data.position_y,
      width: data.width,
      height: data.height,
      sort_index: data.sort_index,
      is_visible: data.is_visible,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async deleteWidget(id: number): Promise<void> {
    await apiClient.delete(widgetPath(`/${id}`));
  },

  async updateWidgetLayout(
    id: number,
    layout: { position_x?: number; position_y?: number; width?: number; height?: number }
  ): Promise<DashboardWidget> {
    const response = await apiClient.patch(widgetPath(`/${id}/layout`), {
      position_x: layout.position_x,
      position_y: layout.position_y,
      width: layout.width,
      height: layout.height,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async updateWidgetTitle(id: number, title: string): Promise<DashboardWidget> {
    const response = await apiClient.patch(widgetPath(`/${id}/title`), { title });
    return extractSingle<DashboardWidget>(response);
  },

  async updateWidgetVisibility(id: number, is_visible: boolean): Promise<DashboardWidget> {
    const response = await apiClient.patch(widgetPath(`/${id}/visibility`), {
      is_visible,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async updateWidgetFilters(id: number, filters: Array<{ field: string; operator: string; value: unknown }>): Promise<DashboardWidget> {
    const response = await apiClient.patch(widgetPath(`/${id}/filters`), {
      filters,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async updateWidgetVisualizationConfig(
    id: number,
    visualization_config: Record<string, unknown>
  ): Promise<DashboardWidget> {
    const response = await apiClient.patch(widgetPath(`/${id}/visualization-config`), {
      visualization_config,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async bulkUpdateLayouts(dashboardId: number, data: BulkLayoutUpdateRequest): Promise<void> {
    await apiClient.put(dashboardPath(`/${dashboardId}/widgets/bulk-layout`), {
      widgets: data.widgets.map((w) => ({
        id: w.id,
        position_x: w.position_x,
        position_y: w.position_y,
        width: w.width,
        height: w.height,
        sort_index: w.sort_index,
      })),
    });
  },

  async getShareDestinations(id: number): Promise<ShareDestination[]> {
    const response = await apiClient.get(widgetPath(`/${id}/share-destinations`));
    return extractData<ShareDestination>(response);
  },

  async cloneWidget(id: number, data: CloneWidgetRequest): Promise<DashboardWidget> {
    const response = await apiClient.post(widgetPath(`/${id}/clone`), {
      target_dashboard_id: String(data.target_dashboard_id),
      position_x: data.position_x ?? 0,
      position_y: data.position_y ?? 0,
      sort_index: data.sort_index ?? 0,
    });
    return extractSingle<DashboardWidget>(response);
  },

  async moveWidget(id: number, data: MoveWidgetRequest): Promise<DashboardWidget> {
    const response = await apiClient.post(widgetPath(`/${id}/move`), {
      target_dashboard_id: data.target_dashboard_id,
      sort_index: data.sort_index,
    });
    return extractSingle<DashboardWidget>(response);
  },
};

// ============================================
// Widget Template API Service
// ============================================

export const widgetTemplateService = {
  async getTemplatesForPage(pageKey: string): Promise<WidgetTemplate[]> {
    const response = await apiClient.get(pagePath(pageKey, `/widget-templates`));
    return extractData<WidgetTemplate>(response);
  },

  async getTemplateByKey(key: string): Promise<WidgetTemplate> {
    const response = await apiClient.get(`/ui/widget-templates/key/${key}`);
    return extractSingle<WidgetTemplate>(response);
  },

  async getTemplate(id: string): Promise<WidgetTemplate> {
    const response = await apiClient.get(`/ui/widget-templates/${id}`);
    return extractSingle<WidgetTemplate>(response);
  },
};

// ============================================
// Page API Service
// ============================================

export const pageService = {
  async listPages(): Promise<Page[]> {
    const response = await apiClient.get(`/ui/pages`);
    return extractData<Page>(response);
  },

  async getPageByKey(pageKey: string): Promise<Page> {
    const response = await apiClient.get(`/ui/pages/${pageKey}`);
    return extractSingle<Page>(response);
  },
};
